import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../partials/Sidebar';
import Header from '../partials/Header';
import { Config } from '../config';
import imageSrc from '../utils/imageData';
import { format, parseISO, isValid } from 'date-fns';
import { PDFDownloadLink } from '@react-pdf/renderer';
import AssessmentReport from '../components/AssessmentReport';
import LoadingSpinner from '../components/LoadingSpinner';
import Chart from 'chart.js/auto';
import { Radar } from 'react-chartjs-2';
import GaugeChart from 'react-gauge-chart';
import html2canvas from 'html2canvas';
import { useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom/client';
import offensiveApi from '../services/offensiveApi';

function Reports() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('gap'); // 'gap' or 'offensive'
  
  // GAP Analysis state
  const [engagements, setEngagements] = useState([]);
  const [selectedEngagement, setSelectedEngagement] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [controls, setControls] = useState([]);
  const [compliance, setCompliance] = useState({ data: {}, meta: {} });
  const [customerLogo, setCustomerLogo] = useState(null);
  const [radarChartImage, setRadarChartImage] = useState(null);
  const [gaugeChartImage, setGaugeChartImage] = useState(null);
  const [isPdfReady, setIsPdfReady] = useState(false);
  const [isCapturingCharts, setIsCapturingCharts] = useState(false);
  
  // Offensive Reports state
  const [offensiveEngagements, setOffensiveEngagements] = useState([]);
  const [selectedOffensiveEngagement, setSelectedOffensiveEngagement] = useState(null);
  const [isLoadingOffensive, setIsLoadingOffensive] = useState(true);
  const [isGeneratingOffensiveReport, setIsGeneratingOffensiveReport] = useState(false);
  const [offensiveReportError, setOffensiveReportError] = useState(null);

  // Hidden charts for capture
  const hiddenChartsRef = React.useRef(null);
  const [chartsReady, setChartsReady] = useState(false);

  // Fetch GAP Analysis engagements
  useEffect(() => {
    const token = localStorage.getItem('token');

    const fetchEngagements = async () => {
      const currentToken = localStorage.getItem('token');
      
      try {
        setIsLoading(true);
        const response = await axios.get(
          `${Config.APIBaseURL}/engagements/`,
          {
            headers: {
              'access_token': currentToken,
              'Content-Type': 'application/json'
            },
            withCredentials: true
          }
        );
        
        // Filter for finished engagements (non-offensive). The list must match
        // routers/offensive.OFFENSIVE_SUBTYPE_IDS — it was written when only
        // 1-4 existed, so wireless (8) and the other later subtypes were
        // appearing in the GRC list as well as the offensive one.
        const OFFENSIVE_SUBTYPES = [1, 2, 3, 4, 6, 20];
        const finishedEngagements = response.data.filter(eng =>
          (eng.status === 'finished' || eng.status === 'submitted') &&
          eng.subtype_id && !OFFENSIVE_SUBTYPES.includes(eng.subtype_id)
        );
        setEngagements(finishedEngagements);
      } catch (error) {
        console.error('Error fetching engagements:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (activeTab === 'gap') {
      fetchEngagements();
    }
  }, [activeTab]);

  // Fetch Offensive engagements
  useEffect(() => {
    const fetchOffensiveEngagements = async () => {
      try {
        setIsLoadingOffensive(true);
        const engagements = await offensiveApi.getEngagements();
        
        // Show all offensive engagements (backend will validate prerequisites when generating)
        // Optionally fetch findings count for display, but don't filter
        const engagementsWithFindings = await Promise.all(
          engagements.map(async (eng) => {
            // Try to get findings count if not already present
            if (eng.findings_count === undefined || eng.findings_count === null) {
              try {
                const findings = await offensiveApi.getOffensiveFindings(eng.id);
                return {
                  ...eng,
                  findings_count: findings?.length || 0
                };
              } catch (err) {
                // If we can't fetch findings, still show the engagement
                console.warn(`Could not fetch findings for engagement ${eng.id}:`, err);
                return {
                  ...eng,
                  findings_count: 0
                };
              }
            }
            return eng;
          })
        );
        
        setOffensiveEngagements(engagementsWithFindings);
      } catch (error) {
        console.error('Error fetching offensive engagements:', error);
        setOffensiveEngagements([]);
      } finally {
        setIsLoadingOffensive(false);
      }
    };

    if (activeTab === 'offensive') {
      fetchOffensiveEngagements();
    }
  }, [activeTab]);

  // Handle offensive report generation
  const handleGenerateOffensiveReport = async (engagement) => {
    if (!engagement) {
      setOffensiveReportError('Please select an engagement');
      return;
    }

    setIsGeneratingOffensiveReport(true);
    setOffensiveReportError(null);

    try {
      const blob = await offensiveApi.generateReport(engagement.id);
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().split('T')[0];
      const engagementCode = engagement.engagement_code || engagement.code || `engagement_${engagement.id}`;
      a.download = `report_${engagementCode}_${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('Report generated successfully');
    } catch (error) {
      console.error('Error generating report:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.message || 
                          'Error generating report. Please try again.';
      setOffensiveReportError(errorMessage);
      setTimeout(() => setOffensiveReportError(null), 10000);
    } finally {
      setIsGeneratingOffensiveReport(false);
    }
  };

  // Update the axiosConfig to get fresh token each time
  const getAxiosConfig = () => ({
    headers: {
      'access_token': localStorage.getItem('token'),
      'Content-Type': 'application/json'
    },
    withCredentials: true
  });

  // Fetch controls and gap results when an engagement is selected
  useEffect(() => {
    if (!selectedEngagement) return;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        const config = getAxiosConfig();
        
        // Fetch controls
        const controlsResponse = await axios.get(
          `${Config.APIBaseURL}/controls/${selectedEngagement.framework.framework_id}`,
          config
        );
        setControls(controlsResponse.data.controls_es);

        // Fetch gap results
        const gapResponse = await axios.get(
          `${Config.APIBaseURL}/gap_results/${selectedEngagement.id}`,
          config
        );
        setCompliance(gapResponse.data);

        // Fetch customer logo
        const customerResponse = await axios.get(
          `${Config.APIBaseURL}/customer/${selectedEngagement.customer.id}`,
          config
        );
        setCustomerLogo(customerResponse.data.logo);

      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/signin');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedEngagement, navigate]);

  // Prepare radar chart data
  const radarLabels = (frameworkId) => {
    switch (frameworkId) {
      case 1: return ['GOBERNAR', 'IDENTIFICAR', 'PROTEGER', 'DETECTAR', 'RESPONDER', 'RECUPERAR'];
      case 4: return ['Inventario y Control de Activos Empresariales', 'Inventario y control de activos de software', 'Protección de datos', 'Configuración segura de los activos y el software de la empresa', 
        'Gestión de cuentas', 'Gestión del control de acceso', 'Gestión continua de vulnerabilidades', 'Gestión de registros de auditoría', 'Protecciones para el correo electrónico y el navegador web', 
        'Defensas contra el malware', 'Recuperación de datos', 'Gestión de la infraestructura de red', 'Supervisión y defensa de la red', 'Concienciación y formación en materia de seguridad', 'Gestión de proveedores de servicios', 
        'Seguridad del software de aplicación', 'Gestión de la respuesta a incidentes', 'Pruebas de Intrusión'];
      case 6: return ['Disposiciones Generales', 'Gestión de riesgos de TIC', 'Gestión, clasificación e informes de incidentes relacionados con las TIC', 'Prueba de resiliencia operacional digital', 
        'Gestión del riesgo de terceros de las TIC', 'Acuerdos de intercambio de información'];
      case 9: return ['Gobernanza de la Ciberseguridad', 'Marco de Gestión de Ciberseguridad', 'Sistemas de Acceso', 'Gestión de Seguridad Técnica', 
        'Amenazas y Gestión de Incidentes', 'Gestión de Seguridad', 'Gestión de Continuidad del Negocio', 'Seguimiento y Mejora de la Seguridad', 'De los Reportes'];
      default: return [];
    }
  };

  const radarDataset = (frameworkId) => {
    try {
      switch (frameworkId) {
        case 1: // NIST
          return [
            compliance.data['GV']?.value || 0,
            compliance.data['ID']?.value || 0,
            compliance.data['PR']?.value || 0,
            compliance.data['DE']?.value || 0,
            compliance.data['RS']?.value || 0,
            compliance.data['RC']?.value || 0
          ];
        case 4: // CIS
          return Array.from({ length: 18 }, (_, i) => {
            const ref = (i + 1).toString();
            const value = compliance.data[ref]?.value;
            return value !== undefined ? value : 0;
          });
        case 6: // DORA
          return Array.from({ length: 6 }, (_, i) => {
            const ref = (i + 1).toString();
            const value = compliance.data[ref]?.value;
            return value !== undefined ? value : 0;
          });
        case 9: // INDOTEL
          return Array.from({ length: 9 }, (_, i) => {
            const ref = (i + 1).toString();
            const value = compliance.data[ref]?.value;
            return value !== undefined ? value : 0;
          });
        default:
          console.warn(`Unknown framework ID: ${frameworkId}`);
          return [];
      }
    } catch (error) {
      console.error('Error preparing radar dataset:', error);
      return [];
    }
  };

  const radarData = {
    labels: selectedEngagement ? radarLabels(selectedEngagement.framework.framework_id) : [],
    datasets: [{
      label: 'Compliance - Cybersecurity Functions',
      data: selectedEngagement ? radarDataset(selectedEngagement.framework.framework_id) : [],
      backgroundColor: 'rgba(49, 66, 163, 0.4)',
      borderColor: 'rgba(49, 66, 163, 1)',
      borderWidth: 3,
      pointBackgroundColor: 'rgba(75, 192, 192, 1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(75, 192, 192, 1)',
    }]
  };

  const radarOptions = {
    scales: {
      r: {
        angleLines: {
          display: true,
          color: 'rgba(128, 128, 128, 0.2)',
        },
        grid: {
          color: 'rgba(128, 128, 128, 0.2)',
        },
        pointLabels: {
          color: '#FFF',
          font: {
            size: selectedEngagement?.framework_id === 4 ? 8 : 11
          }
        },
        suggestedMin: 0,
        suggestedMax: 100,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#FFF',
        },
      },
    },
  };

  // Function to prepare report data
  const prepareReportData = () => {
    if (!selectedEngagement) return [];

    const isAssessable = (control) => { 
      switch (selectedEngagement.framework.framework_id) { 
        case 1: return control.ref.length > 5;
        case 4:
        case 6:
        case 9:
          return control.ref.indexOf('.') > -1;
        default: return false;
      }
    };

    const isSummaryControl = (control) => {
      switch (selectedEngagement.framework.framework_id) {
        case 1: return control.ref.length <= 5;
        case 4:
        case 6:
        case 9:
          return control.ref.indexOf('.') === -1;
        default: return false;
      }
    };

    // Calculate score for summary controls
    const calculateSummaryScore = (controlRef) => {
      const subControls = controls.filter(c => 
        c.ref.startsWith(controlRef) && 
        c.ref !== controlRef && 
        compliance.data[c.ref]?.value
      );
      
      if (subControls.length === 0) return 0;
      
      const totalScore = subControls.reduce((sum, control) => 
        sum + (compliance.data[control.ref]?.value || 0), 0
      );
      
      return Math.round((totalScore / (subControls.length * 3)) * 100);
    };

    // Get all controls, both summary and assessable
    const data = controls.map(control => {
      const isSummary = isSummaryControl(control);
      const countermeasures = compliance.data[control.ref]?.counter || [];
      
      return {
        ref: control.ref,
        domain: control.domain,
        description: control.description,
        value: isSummary ? calculateSummaryScore(control.ref) : compliance.data[control.ref]?.value || 0,
        comments: compliance.data[control.ref]?.comment || '',
        countermeasures: countermeasures,
        isSummary: isSummary
      };
    });

    // Sort by ref to maintain hierarchy
    return data.sort((a, b) => a.ref.localeCompare(b.ref, undefined, { numeric: true }));
  };

  // First, add a styled container for the gauge chart
  const gaugeContainerStyle = {
    width: '360px',  // Increased from 300px
    height: '180px', // Increased from 150px
    backgroundColor: 'white',
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  // Initialize charts when data changes
  useEffect(() => {
    if (selectedEngagement && !isLoading) {
      setIsCapturingCharts(true);
      setIsPdfReady(false);
      setRadarChartImage(null);
      setGaugeChartImage(null);

      const captureCharts = async () => {
        try {
          // Create and render radar chart
          const radarCanvas = document.createElement('canvas');
          radarCanvas.width = 360;  // Match gauge width
          radarCanvas.height = 180; // Match gauge height
          
          const radarCtx = radarCanvas.getContext('2d');
          radarCtx.fillStyle = '#FFFFFF';
          radarCtx.fillRect(0, 0, radarCanvas.width, radarCanvas.height);

          const radarInstance = new Chart(radarCtx, {
            type: 'radar',
            data: radarData,
            options: {
              ...radarOptions,
              responsive: false,
              animation: false,
              plugins: {
                ...radarOptions.plugins,
                legend: {
                  ...radarOptions.plugins.legend,
                  labels: {
                    ...radarOptions.plugins.legend.labels,
                    color: '#000000'
                  }
                }
              },
              scales: {
                r: {
                  ...radarOptions.scales.r,
                  grid: {
                    ...radarOptions.scales.r.grid,
                    color: 'rgba(0, 0, 0, 0.1)'
                  },
                  pointLabels: {
                    ...radarOptions.scales.r.pointLabels,
                    color: '#000000'
                  }
                }
              }
            }
          });

          // Wait for radar chart to render
          await new Promise(resolve => setTimeout(resolve, 500));
          
          try {
            // Get the full data URL and extract just the base64 part
            const radarDataUrl = radarCanvas.toDataURL('image/png');
            const radarBase64 = radarDataUrl.split(',')[1];
            setRadarChartImage(radarBase64);
          } catch (err) {
            console.error('Error capturing radar chart:', err);
          }

          // Cleanup radar chart
          radarInstance.destroy();

          // Create and capture gauge chart
          const gaugeContainer = document.createElement('div');
          gaugeContainer.style.width = '360px';
          gaugeContainer.style.height = '180px';
          gaugeContainer.style.backgroundColor = '#FFFFFF';
          document.body.appendChild(gaugeContainer);

          const gaugeRoot = ReactDOM.createRoot(gaugeContainer);
          gaugeRoot.render(
            <div style={{
              width: '360px',
              height: '180px',
              backgroundColor: '#FFFFFF',
              padding: '10px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              <label className="text-center block mb-2 font-bold" style={{ color: '#000000' }}>
                Nivel de Cumplimiento General
              </label>
              <GaugeChart
                id="gauge-chart-pdf"
                colors={["#DE3163", "#FFFF00", "#27AE60"]}
                arcWidth={0.1}
                needleScale={0.70}
                percent={compliance.meta.genCompliance / 100 || 0}
                nrOfLevels={3}
                animate={false}
                style={{ width: '360px', height: '150px' }}
                textColor="#000000"
              />
            </div>
          );

          // Wait for gauge chart to render
          await new Promise(resolve => setTimeout(resolve, 1000));

          try {
            const gaugeImage = await html2canvas(gaugeContainer, {
              backgroundColor: '#FFFFFF',
              scale: 2,
              logging: false,
              useCORS: true,
              allowTaint: true
            });
            
            // Get the full data URL and extract just the base64 part
            const gaugeDataUrl = gaugeImage.toDataURL('image/png');
            const gaugeBase64 = gaugeDataUrl.split(',')[1];
            setGaugeChartImage(gaugeBase64);
          } catch (err) {
            console.error('Error capturing gauge chart:', err);
          }

          // Cleanup gauge chart
          document.body.removeChild(gaugeContainer);
          
        } catch (error) {
          console.error('Error in chart capture process:', error);
        } finally {
          setIsCapturingCharts(false);
          setIsPdfReady(true);
        }
      };

      captureCharts();
    }
  }, [selectedEngagement, isLoading, compliance.meta.genCompliance]);

  // Update PDF document generation
  const getPDFDocument = () => {
    if (!isPdfReady || !radarChartImage || !gaugeChartImage) {
      console.warn('PDF not ready yet:', { 
        isPdfReady, 
        hasRadarImage: !!radarChartImage, 
        hasGaugeImage: !!gaugeChartImage 
      });
      return null;
    }

    return (
      <AssessmentReport
        data={prepareReportData()}
        customer={selectedEngagement.customer.name || ''}
        framework={selectedEngagement.framework.framework_cat.name || ''}
        startDate={selectedEngagement.start || ''}
        finishDate={selectedEngagement.finish || ''}
        genScore={compliance.meta.genCompliance / 100}
        customerLogo={customerLogo || ''}
        radarImage={radarChartImage || ''}
        gaugeImage={gaugeChartImage || ''}
      />
    );
  };

  // Helper function to safely format dates
  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'N/A';
      const date = parseISO(dateString);
      return isValid(date) ? format(date, 'MMM dd, yyyy') : 'Invalid Date';
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  // Update the customer logo display in the UI
  const getCustomerLogoUrl = (logoData) => {
    return imageSrc(logoData);
  };

  if (isLoading && !selectedEngagement) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] overflow-hidden">

      {/* Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Content area */}
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">

        {/*  Site header */}
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
          {/* Page header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Reports</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Generate compliance and offensive security reports from completed engagements.
            </p>
          </div>

          {/* Tabs */}
          <div className="mb-6 border-b border-slate-200 dark:border-slate-700">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('gap')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'gap'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300'
                }`}
              >
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Gap Analysis
                </div>
              </button>
              <button
                onClick={() => setActiveTab('offensive')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'offensive'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300'
                }`}
              >
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Offensive Security
                </div>
              </button>
            </nav>
          </div>

          {activeTab === 'gap' && (
            <div className="grid grid-cols-12 gap-6">
              {/* Engagement list */}
              <div className="col-span-12 md:col-span-4">
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                  <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
                    <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Completed Assessments
                    </h2>
                  </div>
                  <div className="p-3 max-h-[60vh] overflow-y-auto">
                    {engagements.map(engagement => (
                      <div
                        key={engagement.id}
                        className={`p-3 rounded-lg cursor-pointer mb-2 transition-colors ${
                          selectedEngagement?.id === engagement.id
                            ? 'bg-indigo-600 text-white'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-700/50 text-slate-700 dark:text-slate-300'
                        }`}
                        onClick={() => setSelectedEngagement(engagement)}
                      >
                        <div className="font-semibold text-sm">
                          {engagement.customer.name || 'Unnamed Customer'}
                        </div>
                        <div className="text-xs mt-0.5 opacity-80">
                          {engagement.framework.framework_cat.name || 'No Framework'}
                        </div>
                        <div className="text-[10px] mt-1 opacity-60">
                          Completed: {formatDate(engagement.finish)}
                        </div>
                      </div>
                    ))}
                    {engagements.length === 0 && (
                      <div className="text-center py-8 text-slate-400 dark:text-slate-500">
                        <svg className="w-10 h-10 mx-auto mb-2 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-xs">No completed assessments</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Detail panel */}
              <div className="col-span-12 md:col-span-8">
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                  <div className="px-5 py-4">
                    {!selectedEngagement ? (
                      <div className="text-center py-16 text-slate-400 dark:text-slate-500">
                        <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                        </svg>
                        <p className="text-sm">Select an assessment to generate its report</p>
                      </div>
                    ) : isLoading ? (
                      <div className="text-center py-16">
                        <LoadingSpinner />
                        <p className="mt-3 text-sm text-slate-500">Preparing report data...</p>
                      </div>
                    ) : (
                      <div>
                        {/* Engagement info card */}
                        <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4 mb-5 border border-slate-200 dark:border-slate-700/50">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              {customerLogo && (
                                <img
                                  src={getCustomerLogoUrl(customerLogo)}
                                  alt="Customer Logo"
                                  className="max-h-12 w-auto rounded"
                                />
                              )}
                              <div>
                                <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                                  {selectedEngagement.customer?.name}
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                  {selectedEngagement.framework?.framework_cat?.name}
                                </p>
                              </div>
                            </div>
                            <div className="text-right text-xs text-slate-500 dark:text-slate-400">
                              <div>Started: {selectedEngagement.start}</div>
                              <div>Finished: {selectedEngagement.finish}</div>
                            </div>
                          </div>
                        </div>

                        {/* Download button */}
                        <PDFDownloadLink
                          document={getPDFDocument()}
                          fileName={`gap-analysis-${selectedEngagement.customer.name || 'unnamed'}-${format(new Date(), 'yyyy-MM-dd')}.pdf`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          {({ loading, error }) => {
                            if (error) return 'Error generating PDF';
                            if (isCapturingCharts || !isPdfReady) return 'Preparing charts...';
                            return loading ? 'Generating PDF...' : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Download Report
                              </>
                            );
                          }}
                        </PDFDownloadLink>

                        {/* Hidden charts for PDF capture */}
                        <div ref={hiddenChartsRef} style={{ display: 'none' }}>
                          <div style={{ width: '400px', height: '400px', backgroundColor: 'white' }}>
                            <Radar data={radarData} options={{ ...radarOptions, responsive: false, maintainAspectRatio: false, animation: false }} />
                          </div>
                          <div id="gauge-chart-container" style={{ ...gaugeContainerStyle, position: 'relative', width: '360px', height: '180px' }}>
                            <GaugeChart
                              id="gauge-chart"
                              colors={["#DE3163", "#FFFF00", "#27AE60"]}
                              arcWidth={0.1} needleScale={0.70}
                              percent={compliance.meta.genCompliance / 100 || 0}
                              nrOfLevels={3} animate={false}
                              style={{ width: '360px', height: '150px' }} textColor="#000000"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'offensive' && (
            <div className="grid grid-cols-12 gap-6">
              {/* Engagement list */}
              <div className="col-span-12 md:col-span-4">
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                  <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
                    <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Offensive Engagements
                    </h2>
                  </div>
                  <div className="p-3 max-h-[60vh] overflow-y-auto">
                    {isLoadingOffensive ? (
                      <div className="text-center py-8"><LoadingSpinner /></div>
                    ) : (
                      <>
                        {offensiveEngagements.map(engagement => (
                          <div
                            key={engagement.id}
                            className={`p-3 rounded-lg cursor-pointer mb-2 transition-colors ${
                              selectedOffensiveEngagement?.id === engagement.id
                                ? 'bg-indigo-600 text-white'
                                : 'hover:bg-slate-50 dark:hover:bg-slate-700/50 text-slate-700 dark:text-slate-300'
                            }`}
                            onClick={() => setSelectedOffensiveEngagement(engagement)}
                          >
                            <div className="font-semibold text-sm">
                              {engagement.engagement_code || `Engagement #${engagement.id}`}
                            </div>
                            <div className="text-xs mt-0.5 opacity-80">
                              {engagement.customer?.name || 'No client'}
                            </div>
                            <div className="text-[10px] mt-1 opacity-60">
                              {engagement.findings_count || 0} findings
                            </div>
                          </div>
                        ))}
                        {offensiveEngagements.length === 0 && (
                          <div className="text-center py-8 text-slate-400 dark:text-slate-500">
                            <svg className="w-10 h-10 mx-auto mb-2 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            <p className="text-xs">No engagements with findings</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Detail panel */}
              <div className="col-span-12 md:col-span-8">
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                  <div className="px-5 py-4">
                    {!selectedOffensiveEngagement ? (
                      <div className="text-center py-16 text-slate-400 dark:text-slate-500">
                        <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                        </svg>
                        <p className="text-sm">Select an engagement to generate its report</p>
                      </div>
                    ) : (
                      <div>
                        {offensiveReportError && (
                          <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">
                            {offensiveReportError}
                          </div>
                        )}

                        {/* Engagement info card */}
                        <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4 mb-5 border border-slate-200 dark:border-slate-700/50">
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-500 mb-1">Code</div>
                              <div className="font-semibold text-slate-800 dark:text-slate-100">{selectedOffensiveEngagement.engagement_code}</div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-500 mb-1">Client</div>
                              <div className="font-semibold text-slate-800 dark:text-slate-100">{selectedOffensiveEngagement.customer?.name || 'N/A'}</div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-500 mb-1">Findings</div>
                              <div className="font-semibold text-slate-800 dark:text-slate-100">{selectedOffensiveEngagement.findings_count || 0}</div>
                            </div>
                          </div>
                        </div>

                        {/* Generate button */}
                        <button
                          onClick={() => handleGenerateOffensiveReport(selectedOffensiveEngagement)}
                          disabled={isGeneratingOffensiveReport}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isGeneratingOffensiveReport ? (
                            <>
                              <LoadingSpinner size="sm" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Generate PDF Report
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Reports; 